import React from 'react';
import Image from 'next/image';
import StarRatings from 'react-star-ratings';
import CustomLink from '../CustomLink';
import { ProductURLBuilder } from '@/utils/URLBuilder';
import CustomToast from '../CustomToast';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from 'react-query';
import {WishlistAdd, WishlistDelete} from '@/services/api/wishlist'
import useAuth from '@/hooks/useAuth';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import useUserPreferences from '@/hooks/useUserPreferences';
import { useCurrency } from '../DesktopHeader';

interface ProductCardProps {
  product: any,
  name: string;
  gender: any;
  key?: string | number;
}

const NewProductItem: React.FC<ProductCardProps> = ({ product, name, gender, key }) => {
  const {t} = useTranslation('alerts');
  const {locale} = useRouter();
  const demoName = locale === 'ar' 
    ? product?.demo_name?.ar || '' 
    : product?.demo_name?.en || '';

  const Excerpt = locale === 'ar' 
    ? product?.excerpt?.ar || '' 
    : product?.excerpt?.en || '';
  const splitName = demoName.split('{basmti}');
  const WishlistAddMutation = useMutation(WishlistAdd)
  const WishlistRemoveMutation = useMutation(WishlistDelete)
  const queryClient = useQueryClient()
  const {user} = useAuth('none')
  const router = useRouter();
  const isProductsPage = router.pathname.includes('/products');
  const isNameEntered = name !== (locale === 'ar' ? product?.replace_name?.ar : product?.replace_name?.en)
  
  // Safe localStorage access with useEffect
  const [currency, setCurrency] = React.useState<string>('ILS');
  const [userChoice, setUserChoice] = React.useState({ currency: 'ILS', country: 'Israel' });
  const preferences = useUserPreferences();
  
  const currencySymbol = useCurrency(preferences?.currencyCode || 'ILS');

  React.useEffect(() => {
    // Get user choice from localStorage for currency
    const getUserChoice = () => {
      if (typeof window !== 'undefined') {
        const stored = localStorage.getItem('user-locale-choice');
        return stored ? JSON.parse(stored) : { currency: 'ILS', country: 'Israel' };
      }
      return { currency: 'ILS', country: 'Israel' };
    };

    const getCurrency = () => {
      if (typeof window !== 'undefined') {
        return localStorage.getItem('user-currency-selection') || 'ILS';
      }
      return 'ILS';
    };

    setUserChoice(getUserChoice());
    setCurrency(getCurrency());
  }, []);

  const url = ProductURLBuilder(name ?? (locale === 'ar' ? product?.replace_name?.ar : product?.replace_name?.en), gender, product?.slug, product?.product_type, currency)

  const removeWishlist = (id: number) => {
    if (!user) {
      toast.custom((tel) => <CustomToast t={tel} message={t('login_wishlist')} type='error' />);
      return
    }
    WishlistRemoveMutation.mutate(id, {
      onSuccess() {
        queryClient.invalidateQueries(['wishlist'])
        queryClient.invalidateQueries(['featured_products'])
        queryClient.invalidateQueries(['products'])
        toast.custom((tel) => <CustomToast t={tel} message={t('wishlist_remove')} type='success' />, {
          duration: 5000,
        });
      },
      onError(error: any) {
        toast.custom((t) => <CustomToast t={t} message={error.response.data.message} type='error' />, {
          duration: 4000,
          id: 'Validation Error'
        });
      },
    })
  }
  
  const addToWishlist = (id: number) => {
    if (!user) {
      toast.custom((tel) => <CustomToast t={tel} message={t('login_wishlist')} type='error' />);
      return
    }
    WishlistAddMutation.mutate(id, {
      onSuccess() {
        queryClient.invalidateQueries(['wishlist'])
        queryClient.invalidateQueries(['featured_products'])
        queryClient.invalidateQueries(['products'])
        toast.custom((tel) => <CustomToast t={tel} message={t('wishlist_add')} type='success' />,{
          duration: 5000
        });
      },
      onError(error: any) {
        toast.custom((t) => <CustomToast t={t} message={error.response.data.message} type='error' />,{
          duration: 4000,
          id: 'Validation Error'
        });
      },
    })
  }

  const {t: tProduct} = useTranslation('products');

  // console.log('🎯 Full product object:', JSON.stringify(product, null, 2))
  return (
    <div
      className={`
        bg-white text-center space-y-2
        min-w-[45vw] min-h-[300px]
        md:min-w-[30vw]
        lg:min-w-[18vw]
        py-1 px-2 md:p-2 lg:p-4
        border border-[#DDE2E8] rounded-2xl
        mx-auto flex flex-col justify-around
        prd-item snap-start
      `}
    >
      <div  className='relative w-full cursor-pointer' onClick={()=>{
        const url = ProductURLBuilder(
                    name ?? (locale === 'ar' ? product?.replace_name?.ar : product?.replace_name?.en),
                    gender,
                    product?.slug,
                    product?.product_type,
                    currency
                  )
        // console.log('🔄 Navigating to:', url)
        // console.log('📦 Product slug:', product?.slug)
        router.push(url);
      }}>
        {/* Product Image */}
          <Image
            src={process.env.NEXT_PUBLIC_CDN_URL + '/' + product?.images[0]}
            alt="product"
            width={280}
            height={170}
            className={`mx-auto rounded-lg w-full max-h-[110px] md:max-h-[200px] object-cover `}
          />
        
        {/* Discount Badge */}
        {(product?.discount_percentage > 0 && !isProductsPage) && (
          <div className="bg-red-500 text-white text-xs md:text-sm absolute top-0 right-0 z-10 font-bold px-[8px] md:px-[11px] py-1 rounded-tr-lg w-fit">
            {locale === 'ar' ? `خصم %${product?.discount_percentage}` : `${product?.discount_percentage}% discount`}
          </div>
        )}
        {product?.is_in_wishlist && (
                  <a
                    href="#"
                    onClick={e => {
                      e.preventDefault()
                      removeWishlist(product?.id)
                    }}
                    className={`absolute top-1 left-1 z-10 border border-primary rounded-lg mr-4 flex items-center justify-center p-1 w-6 h-6 md:p-2 md:w-8 md:h-8 lg:hidden`}>
                    <svg
                      width="23"
                      height="24"
                      viewBox="0 0 23 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M20.1357 24C19.7657 23.999 19.3996 23.9247 19.0586 23.7813C18.7175 23.638 18.4083 23.4285 18.1487 23.165L11.9987 17.051L5.84866 23.169C5.45369 23.5697 4.94715 23.8422 4.39508 23.9508C3.843 24.0594 3.27101 23.9992 2.75366 23.778C2.23118 23.5678 1.7842 23.205 1.47112 22.7369C1.15803 22.2688 0.993376 21.7171 0.998658 21.154V5C0.998658 3.67392 1.52544 2.40215 2.46312 1.46447C3.40081 0.526784 4.67258 0 5.99866 0L17.9987 0C18.6553 0 19.3054 0.129329 19.9121 0.380602C20.5187 0.631876 21.0699 1.00017 21.5342 1.46447C21.9985 1.92876 22.3668 2.47996 22.618 3.08658C22.8693 3.69321 22.9987 4.34339 22.9987 5V21.154C23.0043 21.7167 22.8402 22.268 22.5278 22.7361C22.2155 23.2041 21.7694 23.5672 21.2477 23.778C20.8955 23.9253 20.5174 24.0008 20.1357 24Z"
                        fill="#00214D"
                      />
                    </svg>
                  </a>
                )}
                {!product?.is_in_wishlist && (
                  <a
                    href="#"
                    onClick={e => {
                      e.preventDefault()
                      addToWishlist(product?.id)
                    }}
                    className={`absolute top-1 left-1 z-10 border border-primary rounded-lg mr-4 flex items-center justify-center p-1 w-6 h-6 md:p-2 md:w-8 md:h-8 lg:hidden`}>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <g clipPath="url(#clip0_919_2605)">
                        <path
                          d="M20.1371 24C19.7672 23.999 19.4011 23.9247 19.0601 23.7813C18.719 23.638 18.4097 23.4285 18.1501 23.165L12.0001 17.051L5.85012 23.169C5.45515 23.5697 4.94861 23.8422 4.39654 23.9508C3.84447 24.0594 3.27247 23.9992 2.75512 23.778C2.23264 23.5678 1.78567 23.205 1.47258 22.7369C1.15949 22.2688 0.994841 21.7171 1.00012 21.154V5C1.00012 3.67392 1.52691 2.40215 2.46459 1.46447C3.40227 0.526784 4.67404 0 6.00012 0L18.0001 0C18.6567 0 19.3069 0.129329 19.9135 0.380602C20.5202 0.631876 21.0714 1.00017 21.5357 1.46447C22 1.92876 22.3683 2.47996 22.6195 3.08658C22.8708 3.69321 23.0001 4.34339 23.0001 5V21.154C23.0057 21.7167 22.8417 22.268 22.5293 22.7361C22.217 23.2041 21.7709 23.5672 21.2491 23.778C20.8969 23.9253 20.5189 24.0008 20.1371 24ZM6.00012 2C5.20447 2 4.44141 2.31607 3.8788 2.87868C3.31619 3.44129 3.00012 4.20435 3.00012 5V21.154C2.99976 21.3206 3.04879 21.4836 3.14102 21.6224C3.23325 21.7612 3.36455 21.8695 3.51831 21.9337C3.67208 21.9979 3.84143 22.0151 4.00496 21.9831C4.1685 21.9512 4.31888 21.8714 4.43712 21.754L11.3001 14.933C11.4875 14.7468 11.7409 14.6422 12.0051 14.6422C12.2693 14.6422 12.5228 14.7468 12.7101 14.933L19.5651 21.752C19.6834 21.8694 19.8338 21.9492 19.9973 21.9811C20.1608 22.0131 20.3302 21.9959 20.4839 21.9317C20.6377 21.8675 20.769 21.7592 20.8612 21.6204C20.9535 21.4816 21.0025 21.3186 21.0021 21.152V5C21.0021 4.20435 20.6861 3.44129 20.1234 2.87868C19.5608 2.31607 18.7978 2 18.0021 2H6.00012Z"
                          fill="#00214D"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_919_2605">
                          <rect width="24" height="24" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </a>
                )}
      </div>

      {/* product card content */}
      <div className='w-full' dir={`${locale === 'ar' ? 'rtl' : 'ltr'}`}>
        {/* product name and image */}
        <div className='flex w-full items-start justify-between mt-2'>
          {/* Title */}
          <h3 className="text-sm md:text-base font-semibold text-start line-clamp-2 leading-snug h-[2.35rem] md:h-[2.8rem]">
            {splitName[0]}
            <span className={`${isNameEntered ? 'text-success-500' : ''}`}>{name}</span>
            {splitName[1]}
          </h3>

          {/* Avatar */}
          <div className="flex items-center justify-center gap-1 text-sm text-gray-500 w-auto h-auto flex-shrink-0">
            {product?.has_female && !product?.has_male && (
              <Image
                src={'/images/little-girl.png'}
                alt="girl-avatar"
                width={20}
                height={20}
                className="w-5 h-5 md:w-6 md:h-6"
              />
            )}

            {!product?.has_female && product?.has_male && (
              <Image
                src={'/images/little-boy.png'}
                alt="boy-avatar"
                width={20}
                height={20}
                className="w-5 h-5 md:w-6 md:h-6"
              />
            )}

            {product?.has_female && product?.has_male && (
              <>
                <Image
                  src={'/images/little-girl.png'}
                  alt="girl-avatar"
                  width={20}
                  height={20}
                  className="w-5 h-5 md:w-6 md:h-6"
                />
                <Image
                  src={'/images/little-boy.png'}
                  alt="boy-avatar"
                  width={20}
                  height={20}
                  className="w-5 h-5 md:w-6 md:h-6"
                />
              </>
            )}
          </div>
        </div>

        {/* Description */}
        <p className='w-full text-start mt-2 lg:mt-3 text-xs md:text-sm font-light text-[#93A3B0] h-[1.65rem] md:h-[2rem]'>
          {(Excerpt
            ? Excerpt
            : product?.name
            ? product?.name
            : 'قصتنا لمساعدة الطفل على التحمس لاول يوم دراسة'
          ).slice(0, 35) +
            ((Excerpt
              ? Excerpt
              : product?.name
              ? product?.name
              : 'قصتنا لمساعدة الطفل على التحمس لاول يوم دراسة'
            ).length > 35
              ? '...'
              : '')}
        </p>

        {/* Pricing */}
        <div className="pricing flex items-center mt-2 lg:mt-3">
          <p className='text-sm md:text-base lg:hidden font-light'>{tProduct('priceTextProductCard')}</p>
          <h2 className="text-[#695D17] lg:text-black font-normal">
            <span>{currencySymbol.symbol}</span>
            {product?.front_price?.amount}
          </h2>
          {product?.has_sale && (
            <h3 className={`old-price text-red ${locale === 'ar' ? 'mr-2' : 'ml-2'}  line-through text-sm font-light`}>
              <span>{currencySymbol.symbol}</span>
              {/* Calculate original price before discount */}
              {product?.price.amount}
            </h3>
          )}
        </div>

        {/* Action Buttons */}
        <div className="info-actions flex mt-2 lg:mt-6">
                <CustomLink
                  href={ProductURLBuilder(
                    name ?? (locale === 'ar' ? product?.replace_name?.ar : product?.replace_name?.en),
                    gender,
                    product?.slug,
                    product?.product_type,
                    currency
                  )}
                  bg="primary"
                  classes={['w-full', 'py-2', 'text-sm', 'md:text-lg']}>
                  <>
                    <span>
                      {product?.product_type == 1
                          ? `${tProduct('customizeStory')}`
                        : `${tProduct('customizeStory')}`}
                    </span>
                  </>
                </CustomLink>
                {product?.is_in_wishlist && (
                  <a
                    href="#"
                    onClick={e => {
                      e.preventDefault()
                      removeWishlist(product?.id)
                    }}
                    className={`border border-primary rounded-lg ${locale === 'ar' ? 'mr-4': 'ml-4'} hidden lg:flex items-center justify-center p-3`}>
                    <svg
                      width="23"
                      height="24"
                      viewBox="0 0 23 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M20.1357 24C19.7657 23.999 19.3996 23.9247 19.0586 23.7813C18.7175 23.638 18.4083 23.4285 18.1487 23.165L11.9987 17.051L5.84866 23.169C5.45369 23.5697 4.94715 23.8422 4.39508 23.9508C3.843 24.0594 3.27101 23.9992 2.75366 23.778C2.23118 23.5678 1.7842 23.205 1.47112 22.7369C1.15803 22.2688 0.993376 21.7171 0.998658 21.154V5C0.998658 3.67392 1.52544 2.40215 2.46312 1.46447C3.40081 0.526784 4.67258 0 5.99866 0L17.9987 0C18.6553 0 19.3054 0.129329 19.9121 0.380602C20.5187 0.631876 21.0699 1.00017 21.5342 1.46447C21.9985 1.92876 22.3668 2.47996 22.618 3.08658C22.8693 3.69321 22.9987 4.34339 22.9987 5V21.154C23.0043 21.7167 22.8402 22.268 22.5278 22.7361C22.2155 23.2041 21.7694 23.5672 21.2477 23.778C20.8955 23.9253 20.5174 24.0008 20.1357 24Z"
                        fill="#00214D"
                      />
                    </svg>
                  </a>
                )}
                {!product?.is_in_wishlist && (
                  <a
                    href="#"
                    onClick={e => {
                      e.preventDefault()
                      addToWishlist(product?.id)
                    }}
                    className={`border border-primary rounded-lg ${locale === 'ar' ? 'mr-4': 'ml-4'} hidden lg:flex items-center justify-center p-3`}>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <g clipPath="url(#clip0_919_2605)">
                        <path
                          d="M20.1371 24C19.7672 23.999 19.4011 23.9247 19.0601 23.7813C18.719 23.638 18.4097 23.4285 18.1501 23.165L12.0001 17.051L5.85012 23.169C5.45515 23.5697 4.94861 23.8422 4.39654 23.9508C3.84447 24.0594 3.27247 23.9992 2.75512 23.778C2.23264 23.5678 1.78567 23.205 1.47258 22.7369C1.15949 22.2688 0.994841 21.7171 1.00012 21.154V5C1.00012 3.67392 1.52691 2.40215 2.46459 1.46447C3.40227 0.526784 4.67404 0 6.00012 0L18.0001 0C18.6567 0 19.3069 0.129329 19.9135 0.380602C20.5202 0.631876 21.0714 1.00017 21.5357 1.46447C22 1.92876 22.3683 2.47996 22.6195 3.08658C22.8708 3.69321 23.0001 4.34339 23.0001 5V21.154C23.0057 21.7167 22.8417 22.268 22.5293 22.7361C22.217 23.2041 21.7709 23.5672 21.2491 23.778C20.8969 23.9253 20.5189 24.0008 20.1371 24ZM6.00012 2C5.20447 2 4.44141 2.31607 3.8788 2.87868C3.31619 3.44129 3.00012 4.20435 3.00012 5V21.154C2.99976 21.3206 3.04879 21.4836 3.14102 21.6224C3.23325 21.7612 3.36455 21.8695 3.51831 21.9337C3.67208 21.9979 3.84143 22.0151 4.00496 21.9831C4.1685 21.9512 4.31888 21.8714 4.43712 21.754L11.3001 14.933C11.4875 14.7468 11.7409 14.6422 12.0051 14.6422C12.2693 14.6422 12.5228 14.7468 12.7101 14.933L19.5651 21.752C19.6834 21.8694 19.8338 21.9492 19.9973 21.9811C20.1608 22.0131 20.3302 21.9959 20.4839 21.9317C20.6377 21.8675 20.769 21.7592 20.8612 21.6204C20.9535 21.4816 21.0025 21.3186 21.0021 21.152V5C21.0021 4.20435 20.6861 3.44129 20.1234 2.87868C19.5608 2.31607 18.7978 2 18.0021 2H6.00012Z"
                          fill="#00214D"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_919_2605">
                          <rect width="24" height="24" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </a>
                )}
        </div>

      </div>
    </div>
  );
};

export default NewProductItem;